import React, { useEffect, useState, useRef } from 'react';
import { Button, Typography } from 'src/components/common';
import { InputField } from 'src/components/common/formFields';
import { CompanyAPIService } from 'src/services/company.service';
import RocketIcon from 'src/assets/images/banner/RocketIcon.svg';
import { openPopup } from 'src/store/common/common.slice';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { TGetCompany, TCreateCompanyRequest, TEditCompanyRequest, TGetCompanyPresignedUrlActionRequest } from 'src/services/apiEndpoint.types.ts';
import { useAppDispatch, useAppSelector } from 'src/store/hooks';
import { createCompany, editCompany, getCompanyPresignedUrl, getCompany } from 'src/store/company/company.slice';
import { StorageUtils } from 'src/utils';
import { TrashIcon, UploadIcon } from 'src/components/common/common.icons';
import { useUploadMedia, useGetIdentity } from 'src/hooks';
import styles from './StartupOnboarding.module.scss';
import Header from 'src/components/demo-day/header/Header';
import htractionLogo from 'src/assets/images/logo/htractionLogowhite.png';
import styled from 'styled-components';

const highlightStats = [
  { value: '20+', label: 'Investor partners' },
  { value: '50+', label: 'Mentor & industry experts' },
  { value: '$1M+', label: 'Successful funds closed' },
  { value: '100+', label: 'Value partners' }
];

const StartupOnboarding = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const appDispatch = useAppDispatch();
  const { getIdentities } = useGetIdentity();
  const { authenticatedId, authenticatedEmail } = getIdentities();
  const companyServiceRef = React.useRef<CompanyAPIService | null>(null);
  const [loading, setLoading] = useState(false);
  const [companyLogoData, setCompanyLogoData] = useState<string | ArrayBuffer | null>(null);
  const [comp, setComp] = useState<TGetCompany | null>(null);
  const uploadImageRef = useRef<HTMLInputElement>(null);
  const selectedImageRef = useRef<HTMLImageElement>(null);
  const dragDropRef = useRef<HTMLDivElement>(null);
  const { handleUploadMedia } = useUploadMedia();
  const { companyProfilePic, myCompany } = useAppSelector((store) => store.company);

  const [formData, setFormData] = useState<{
    startupName: string;
    whatsappNumber: string;
    companyEmailAddress: string;
    termsAccepted: boolean;
  }>({
    startupName: '',
    whatsappNumber: '',
    companyEmailAddress: authenticatedEmail || '',
    termsAccepted: false
  });

  // Update form data when company data changes
  useEffect(() => {
    if (myCompany?.id) {
      setFormData({
        startupName: myCompany.name || '',
        whatsappNumber: myCompany.contactNumber ? String(myCompany.contactNumber) : '',
        companyEmailAddress: myCompany.companyEmailAddress || authenticatedEmail || '',
        termsAccepted: myCompany.termsAndConditions || false
      });
    }
  }, [myCompany, authenticatedEmail]);

  // Initialize companyService and fetch company data
  useEffect(() => {
    companyServiceRef.current = new CompanyAPIService();

    const fetchCompanyData = async () => {
      if (!authenticatedId) return;
      
      try {
        await appDispatch(getCompany({ id: authenticatedId }));
      } catch (error) {
        console.error('Error fetching company:', error);
      }
    };

    fetchCompanyData();
  }, [authenticatedId, appDispatch]);

  const handleInputChange = (field: keyof typeof formData) => (value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const openImageFileManager = async () => {
    if (uploadImageRef.current) {
      uploadImageRef.current.click();
    }
  };

  const onMediaLoad = (data: string | ArrayBuffer | null) => {
    setCompanyLogoData(data);
  };

  const removeCompanyLogo = () => {
    setCompanyLogoData(null);
  };

  const renderSelectedImage = () => {
    const ele = selectedImageRef.current;
    if (!ele || !companyLogoData) return;

    if (typeof companyLogoData === 'string') {
      ele.src = companyLogoData;
    } else {
      const blob = new Blob([companyLogoData], { type: 'image/jpeg' });
      const imageUrl = URL.createObjectURL(blob);
      ele.src = imageUrl;
    }
  };

  // Initialize logo if it exists
  useEffect(() => {
    if (companyProfilePic) {
      setCompanyLogoData(companyProfilePic);
    }
  }, [companyProfilePic]);

  useEffect(() => {
    renderSelectedImage();
  }, [companyLogoData]);

  const handleSubmit = async () => {
    if (!formData.startupName || !formData.whatsappNumber || !formData.companyEmailAddress || !formData.termsAccepted) {
      dispatch(
        openPopup({
          popupMessage: 'Please fill all required fields and accept terms & conditions',
          popupType: 'error'
        })
      );
      return;
    }

    // Phone number validation
    if (!/^\d{10}$/.test(formData.whatsappNumber)) {
      dispatch(
        openPopup({
          popupMessage: 'Please enter a valid 10-digit phone number',
          popupType: 'error'
        })
      );
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.companyEmailAddress)) {
      dispatch(
        openPopup({
          popupMessage: 'Please enter a valid email address',
          popupType: 'error'
        })
      );
      return;
    }

    try {
      setLoading(true);
      let response;
      let companyId;

      if (myCompany?.id) {
        // If company exists, update it
        companyId = myCompany.id;
        const editCompanyData: TEditCompanyRequest = {
          id: myCompany.id,
          name: formData.startupName,
          type: 'Start up',
          companyEmailAddress: formData.companyEmailAddress,
          contactNumber: parseInt(formData.whatsappNumber.replace(/\D/g, ''), 10),
          termsAndConditions: formData.termsAccepted,
          introduction: myCompany.introduction || '',
          foundedYear: myCompany.foundedYear || 0,
          website: myCompany.website || 'N/A',
          about: myCompany.about || 'N/A',
          contactUsUrl: myCompany.contactUsUrl || '',
          addressLine1: myCompany.addressLine1 || '',
          addressLine2: myCompany.addressLine2 || '',
          country: myCompany.country || '',
          state: myCompany.state || '',
          city: myCompany.city || '',
          zipCode: myCompany.zipCode || '',
          industry: myCompany.industry || '',
          linkedin: myCompany.linkedin || '',
          facebook: myCompany.facebook || '',
          twitter: myCompany.twitter || '',
          instagram: myCompany.instagram || ''
        };
        await appDispatch(editCompany(editCompanyData));
      } else {
        // If company doesn't exist, create it
        const createCompanyData: TCreateCompanyRequest = {
          email: authenticatedEmail,
          name: formData.startupName,
          contactNumber: parseInt(formData.whatsappNumber.replace(/\D/g, ''), 10),
          type: 'Start up',
          companyEmailAddress: formData.companyEmailAddress,
          termsAndConditions: formData.termsAccepted,
          consentTimeStamp: new Date().toISOString(),
          navigateTo: '/score'
        };
        await appDispatch(createCompany(createCompanyData));
        companyId = StorageUtils.get('companyId');
        if (!companyId) {
          setLoading(false);
          return;
        }
      }

      if (companyLogoData && companyId) {
        try {
          const uploadProfilePicturePayload: TGetCompanyPresignedUrlActionRequest = {
            id: companyId as string,
            data: companyLogoData,
            showPopup: false
          };
          await appDispatch(getCompanyPresignedUrl(uploadProfilePicturePayload));
        } catch (logoError) {
          console.error('Error uploading logo:', logoError);
          dispatch(
            openPopup({
              popupMessage: 'Company details saved but there was an error uploading the logo.',
              popupType: 'warning'
            })
          );
        }
      }

      await appDispatch(getCompany({ id: companyId as string }));

      // Navigate to /score if terms are accepted
      if (formData.termsAccepted) {
        navigate('/score');
      }
    } catch (error) {
      console.error('Error saving company details:', error);
      dispatch(
        openPopup({
          popupMessage: 'An error occurred while saving details',
          popupType: 'error'
        })
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* <Header /> */}
      <div className={styles.onboardingContainer}>
      <div className={styles.leftSection}>
        <div className={styles.logoContainer}>
          <img src={htractionLogo} alt="htraction" className={styles.logo} />
        </div>
        <div className={styles.content}>
          <HighlightsSection>
            <HighlightsLabel>Trusted By Top Investors & Founders</HighlightsLabel>
            <HighlightsGrid>
              {highlightStats.map((stat) => (
                <HighlightCard key={stat.label}>
                  <HighlightValue>{stat.value}</HighlightValue>
                  <HighlightTitle>{stat.label}</HighlightTitle>
                </HighlightCard>
              ))}
            </HighlightsGrid>
          </HighlightsSection>
        </div>
      </div>

      <div className={styles.rightSection}>
        <div className={styles.formContainer}>
          <div className={styles.formHeader}>
            <div className={styles.rocketIcon}>
              <img src={RocketIcon} alt="Rocket" />
            </div>
            <h4>Setup Your Company Profile</h4>
          </div>

          <div className={styles.formContent}>
            <div className={styles.formField}>
              <InputField
                id="startup-name"
                label="Company Name"
                value={formData.startupName}
                onChange={(e) => handleInputChange('startupName')(e.target.value)}
                placeholder="Enter Your Company"
                required
              />
            </div>

            <div className={styles.formField}>
              <InputField
                id="company-email"
                label="Company Email"
                value={formData.companyEmailAddress}
                onChange={(e) => handleInputChange('companyEmailAddress')(e.target.value)}
                placeholder="Enter Your Company Email"
                type="email"
                required
              />
            </div>

            <div className={styles.formField}>
              <InputField
                id="whatsapp-number"
                label="Whatsapp Number"
                value={formData.whatsappNumber}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, '').slice(0, 10);
                  handleInputChange('whatsappNumber')(value);
                }}
                onKeyPress={(e) => {
                  if (!/[0-9]/.test(e.key)) {
                    e.preventDefault();
                  }
                }}
                placeholder="Enter your phonenumber"
                type="tel"
                maxLength={10}
                pattern="[0-9]{10}"
                required
              />
            </div>

            <div className={styles.uploadSection}>
              <Typography as="p" variant="caption" fontWeight="bold">
                Logo
              </Typography>
              <div
                className={styles.uploadLogoContainer}
                draggable
                onClick={openImageFileManager}
                ref={dragDropRef}
                onDrop={(e) => {
                  e.preventDefault();
                  handleUploadMedia({ e, onLoad: onMediaLoad, mediaType: 'image', dragDrop: true });
                }}
                onDragOver={(e) => {
                  e.preventDefault();
                }}
              >
                {companyLogoData ? (
                  <div className={styles.selectedImage}>
                    <div
                      className={styles.deleteButton}
                      onClick={(e) => {
                        e.stopPropagation();
                        removeCompanyLogo();
                      }}
                    >
                      <TrashIcon />
                    </div>
                    <img alt="uploaded logo" ref={selectedImageRef} />
                  </div>
                ) : (
                  <span className={styles.dragDropArea}>
                    <UploadIcon />
                    <Typography as="p" variant="body-2" fontWeight="regular">
                      Choose a file or drag & drop it here
                    </Typography>
                  </span>
                )}
                <input
                  type="file"
                  hidden
                  ref={uploadImageRef}
                  accept=".bmp,.jpg,.jpeg,.png"
                  className={styles.selectPostImage}
                  onChange={(e) => {
                    handleUploadMedia({ e, onLoad: onMediaLoad, mediaType: 'image' });
                  }}
                />
              </div>
            </div>

            <div className={styles.termsContainer}>
              <input
                type="checkbox"
                id="terms"
                checked={formData.termsAccepted}
                onChange={(e) => handleInputChange('termsAccepted')(e.target.checked)}
              />
              <label htmlFor="terms" className={styles.termsText}>
                I accept the <a href='/termsandconditions' target="_blank" rel="noopener noreferrer" className={styles.termsLink}>terms & conditions</a>
              </label>
            </div>

            <Button
              variant="primary"
              onClick={handleSubmit}
              disabled={loading}
              className={styles.submitButton}
            >
              <span style={{ opacity: loading ? 0 : 1 }}>
                Save & Continue
              </span>
              {loading && (
                <div className={styles.spinner} />
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default StartupOnboarding;
const HighlightsSection = styled.div`
  margin-top: 32px;
  width: 100%;
`;

const HighlightsLabel = styled.span`
  display: inline-flex;
  padding: 8px 18px;
  border-radius: 999px;
  background: linear-gradient(90deg, rgba(255, 255, 255, 0.9), rgba(255, 224, 200, 0.26));
  color: #ff5924;
  border: 0.5px solid rgba(255, 157, 133, 0.6);
  box-shadow: 0 0px 40px rgba(162, 42, 1, 0.12);
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.08em;
`;

const HighlightsGrid = styled.div`
  margin-top: 28px;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;

  @media (max-width: 600px) {
    grid-template-columns: 1fr 1fr;
  }

  @media (max-width: 420px) {
    grid-template-columns: 1fr;
  }
`;

const HighlightCard = styled.div`
  border-radius: 18px;
  padding: 28px 20px;
  text-align: center;
  background:linear-gradient(120deg, rgba(255, 255, 255, 0.26), rgba(255, 224, 200, 0.1));
  border: 0.5px solid rgba(255, 255, 255, 0.85);
  transition: transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease;

  &:hover {
    transform: translateY(-6px);
    box-shadow: 0 25px 55px rgba(255, 89, 36, 0.2);
    border: 2px solid rgb(255, 255, 255, 0.8);
    color: #000000;
  }
`;

const HighlightValue = styled.span`
  font-size: 40px;
  font-weight: 700;
  display: block;
  background: linear-gradient(90deg,rgb(255, 255, 255),rgb(255, 255, 255));
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const HighlightTitle = styled.p`
  margin-top: 6px;
  font-size: 15px;
  font-weight: 600;
  color:rgb(255, 255, 255);
`;
